import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  FiSearch, FiGrid, FiUsers, FiLayers, FiMessageSquare, 
  FiHelpCircle, FiSettings, FiChevronDown, FiChevronsLeft, 
  FiChevronsRight, FiLogOut, FiUser, FiLock, FiChevronUp, FiColumns,
  FiDatabase, FiBook, FiFileText, FiTrello, FiServer, FiCheckCircle, FiArchive
} from "react-icons/fi";

export default function Sidebar({
  permissions = [],
  user = {},
  handleLogout,
  onOpenChangePassword,
}) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [menuSearch, setMenuSearch] = useState("");

  // Added admission to initial state
  const [openSubmenus, setOpenSubmenus] = useState({
    academic: false,
    admission: false,
    employee: false,
    student: false,
  });

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [activeTooltip, setActiveTooltip] = useState(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper function to verify permissions
  const hasPerm = (perm) => permissions.includes(perm);

  const toggleSubmenu = (menuKey) => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const matchesSearch = (text) => 
    text.toLowerCase().includes(menuSearch.toLowerCase());

  const handleMouseEnter = (e, title) => {
    if (!isCollapsed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTooltip({
      title,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  const isSearching = menuSearch.trim().length > 0;

  return (
    <aside
      className={`bg-white border-r border-zinc-200/80 no-print print:hidden font-dynamic-title flex flex-col shrink-0 text-zinc-600 text-xs font-medium z-20 transition-all duration-300 ease-in-out relative ${
        isCollapsed ? "w-16" : "w-full md:w-60"
      }`}
    >
      {/* Header & Toggle */}
      <div className="h-14 px-3.5 flex items-center justify-between border-b border-zinc-100">
        {!isCollapsed && (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-6 h-6 rounded-md bg-[#002855] text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
              C
            </div>
            <span className="font-bold text-zinc-900 text-sm tracking-tight truncate">
              Cambodia Adventist International School
            </span>
          </div>
        )}

        <button
          onClick={() => {
            setIsCollapsed(!isCollapsed);
            setUserMenuOpen(false);
            setActiveTooltip(null);
          }}
          onMouseEnter={(e) => handleMouseEnter(e, isCollapsed ? "Expand Sidebar" : "Collapse Sidebar")}
          onMouseLeave={handleMouseLeave}
          className={`p-1.5 rounded-lg text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors ${
            isCollapsed ? "mx-auto" : ""
          }`}
        >
          {isCollapsed ? <FiChevronsRight className="w-4 h-4" /> : <FiChevronsLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation Search */}
      {!isCollapsed && (
        <div className="p-2.5 border-b border-zinc-100">
          <div className="relative w-full">
            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs" />
            <input
              type="text"
              placeholder="Search menu..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 text-xs bg-zinc-50 border border-zinc-200/80 rounded-md focus:outline-none focus:bg-white focus:border-zinc-300 transition-all placeholder:text-zinc-400"
            />
          </div>
        </div>
      )}

      {/* Scrollable Navigation Container */}
      <div className="p-2.5 flex-1 overflow-y-auto space-y-4">

        {(hasPerm("administration_navigation")) && (
        <div>
          {!isCollapsed && (
            <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
              Administration Navigation
            </p>
          )}
          <div className="space-y-0.5">
            
            {/* Overview */}
            {matchesSearch("Overview") && (
              <Link 
                to="/overview"
                onMouseEnter={(e) => handleMouseEnter(e, "Overview")}
                onMouseLeave={handleMouseLeave}
                className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg transition-colors ${
                  location.pathname === "/overview"
                    ? "text-zinc-900 bg-zinc-100 font-semibold"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`}
              >
                <FiGrid className={`w-4 h-4 shrink-0 ${location.pathname === "/overview" ? "text-zinc-700" : ""}`} />
                {!isCollapsed && <span>Overview</span>}
              </Link>
            )}

            {/* Academic Manager */}
            {hasPerm("can_view_academic_page") && matchesSearch("academic") && (
              <Link 
                to="/academic"
                onMouseEnter={(e) => handleMouseEnter(e, "academic")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiServer className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Academic</span>}
              </Link>
            )}

            {/* Admission Section */}
            {hasPerm("can_view_admission") && (matchesSearch("Admission") || matchesSearch("New Student Registration") || matchesSearch("Returning Student")) && (
              <div>
                <button
                  onMouseEnter={(e) => handleMouseEnter(e, "Admission")}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => toggleSubmenu("admission")}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <FiFileText className="w-4 h-4 shrink-0" />
                    {!isCollapsed && <span>Admission</span>}
                  </div>
                  {!isCollapsed && (
                    <FiChevronDown
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                        openSubmenus.admission || isSearching ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>

                {(openSubmenus.admission || isSearching) && !isCollapsed && (
                  <div className="pl-6 pt-0.5 space-y-0.5 border-l border-zinc-200 ml-4 my-0.5">
                    {hasPerm("can_view_new_student_registration") && matchesSearch("New Student Registration") && (
                      <Link to="/registrar" className="block w-full text-left py-1 text-zinc-500 hover:text-zinc-900 text-[11px] truncate">
                        New Student Registration
                      </Link>
                    )}
                    {hasPerm("can_view_new_student_registration") && matchesSearch("Returning Student") && (
                      <Link to="/registrar" className="block w-full text-left py-1 text-zinc-500 hover:text-zinc-900 text-[11px] truncate">
                        Returning Student
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}

            {hasPerm("can_view_admin_classroom") && matchesSearch("classroom") && (
              <Link 
                to="/classroom_manager"
                onMouseEnter={(e) => handleMouseEnter(e, "classroom")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiColumns  className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Classroom</span>}
              </Link>
            )}

            {/* Employee Manager */}
            {hasPerm("can_view_employee_page") && matchesSearch("employee") && (
              <Link 
                to="/employees"
                onMouseEnter={(e) => handleMouseEnter(e, "employee")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiUsers className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Employee</span>}
              </Link>
            )}

            {/* Inventory Section */}
            {hasPerm("can_view_inventory") && matchesSearch("Inventory") && (
              <Link 
                to="/inventory"
                onMouseEnter={(e) => handleMouseEnter(e, "Inventory")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiDatabase className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Inventory</span>}
              </Link>
            )}

            {/* Library Section */}
            {hasPerm("can_view_library") && matchesSearch("Library") && (
              <Link 
                to="/library"
                onMouseEnter={(e) => handleMouseEnter(e, "Library")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiBook className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Library</span>}
              </Link>
            )}

            {hasPerm("can_view_student_page") && matchesSearch("student") && (
              <Link 
                to="/students"
                onMouseEnter={(e) => handleMouseEnter(e, "student")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiUsers className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Student</span>}
              </Link>
            )}

            {/* Student Section 
            {hasPerm("can_view_student") && (matchesSearch("Student") || matchesSearch("Student Roster") || matchesSearch("Uniform Infraction")) && (
              <div>
                <button
                  onMouseEnter={(e) => handleMouseEnter(e, "Student")}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => toggleSubmenu("student")}
                  className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                >
                  <div className="flex items-center gap-2.5">
                    <FiUsers className="w-4 h-4 shrink-0" />
                    {!isCollapsed && <span>Student</span>}
                  </div>
                  {!isCollapsed && (
                    <FiChevronDown
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform duration-200 ${
                        openSubmenus.student || isSearching ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>

                {(openSubmenus.student || isSearching) && !isCollapsed && (
                  <div className="pl-6 pt-0.5 space-y-0.5 border-l border-zinc-200 ml-4 my-0.5">
                    {matchesSearch("Student Roster") && (
                      <Link to="/students" className="block w-full text-left py-1 text-zinc-500 hover:text-zinc-900 text-[11px] truncate">
                        Student List
                      </Link>
                    )}
                    {hasPerm("can_view_stuudent_absent") && matchesSearch("Student Absent Alert") && (
                      <Link to="/students/absent" className="block w-full text-left py-1 text-zinc-500 hover:text-zinc-900 text-[11px] truncate">
                        Student Absent Alert
                      </Link>
                    )}
                    {hasPerm("can_view_student_late") && matchesSearch("Late Arrival") && (
                      <Link to="/students/uniform" className="block w-full text-left py-1 text-zinc-500 hover:text-zinc-900 text-[11px] truncate">
                        Late Arrival
                      </Link>
                    )}
                    {hasPerm("can_view_uniform") && matchesSearch("Uniform Infraction") && (
                      <Link to="/students/uniform" className="block w-full text-left py-1 text-zinc-500 hover:text-zinc-900 text-[11px] truncate">
                        Uniform Infraction
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
            */}

            {hasPerm("can_view_attendance") && matchesSearch("attendance") && (
              <Link 
                to="/attendance"
                onMouseEnter={(e) => handleMouseEnter(e, "attendance")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiCheckCircle className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Take Attendance</span>}
              </Link>
            )}
          </div>
        </div>
        )}

        {/* Library */}
        {(hasPerm("can_view_library")) && (
          <div>
            {!isCollapsed && (
              <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                Library
              </p>
            )}

            {/* Library*/}
            {hasPerm("can_view_library") && matchesSearch("library") && (
              <Link 
                to="/library"
                onMouseEnter={(e) => handleMouseEnter(e, "library")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiBook className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Library</span>}
              </Link>
            )}
          </div>
        )}
        
        {/* Teacher Navigation */}
        {(hasPerm("teacher_navigation")) && (
          <div>
            {!isCollapsed && (
              <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                Faculty Navigation
              </p>
            )}

            {/* Teacher Classroom */}
            {hasPerm("teacher_classroom") && matchesSearch("Classroom") && (
              <Link 
                to="/teacher/classroom"
                onMouseEnter={(e) => handleMouseEnter(e, "Classroom")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiTrello className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Classroom</span>}
              </Link>
            )}

            {/* Teacher Student List */}
            {hasPerm("teacher_can_view_student_list") && matchesSearch("Student") && (
              <Link 
                to="/teacher/student/list"
                onMouseEnter={(e) => handleMouseEnter(e, "Library")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiUsers className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Student</span>}
              </Link>
            )}
          </div>
        )}

        {/* Report Manager */}
        {(hasPerm("administration_report_manager")) && (
          <div>
            {!isCollapsed && (
              <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                Report Manager
              </p>
            )}

            {/* Report Manager */}
            {hasPerm("can_view_report_manager") && matchesSearch("Report") && (
              <Link 
                to="/report_manager/"
                onMouseEnter={(e) => handleMouseEnter(e, "Report")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiArchive className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Report</span>}
              </Link>
            )}
          </div>
        )}

        {/* Security & Permissions Section */}
        {(hasPerm("can_view_user_list") || hasPerm("can_view_account_lock")) && (
          <div>
            {!isCollapsed && (
              <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                Security & Permission
              </p>
            )}
            <div className="space-y-0.5">
              {hasPerm("can_view_user_list") && matchesSearch("User Account") && (
                <Link 
                  to="/users"
                  onMouseEnter={(e) => handleMouseEnter(e, "User Account")}
                  onMouseLeave={handleMouseLeave}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                >
                  <FiUser className="w-4 h-4 shrink-0" />
                  {!isCollapsed && <span>User Account</span>}
                </Link>
              )}
              {hasPerm("can_view_account_lock") && matchesSearch("User Locked") && (
                <Link 
                  to="/account/locked"
                  onMouseEnter={(e) => handleMouseEnter(e, "User Locked")}
                  onMouseLeave={handleMouseLeave}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
                >
                  <FiLock className="w-4 h-4 shrink-0" />
                  {!isCollapsed && <span>User Locked</span>}
                </Link>
              )}
            </div>
          </div>
        )}

        {(hasPerm("can_configure_system") || hasPerm("can_view_account_lock")) && (
          <div>
            {!isCollapsed && (
              <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                System & Configuration
              </p>
            )}
            {matchesSearch("Settings") && (
              <Link 
                to="/configuration"
                onMouseEnter={(e) => handleMouseEnter(e, "Settings")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiSettings className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Configuration</span>}
              </Link>
            )}
          </div>
        )}

        {/* Support Section */}
        <div>
          {!isCollapsed && (
            <p className="px-2 mb-1.5 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
              Support
            </p>
          )}
          <div className="space-y-0.5">
            {matchesSearch("Feedback") && (
              <Link 
                to="/support/feedback"
                onMouseEnter={(e) => handleMouseEnter(e, "Feedback")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiMessageSquare className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Feedback</span>}
              </Link>
            )}
            {matchesSearch("Help & Support") && (
              <Link 
                to="/support/help"
                onMouseEnter={(e) => handleMouseEnter(e, "Help & Support")}
                onMouseLeave={handleMouseLeave}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
              >
                <FiHelpCircle className="w-4 h-4 shrink-0" />
                {!isCollapsed && <span>Help & Support</span>}
              </Link>
            )}
          </div>
        </div>

      </div>

      {/* User Footer Account Button */}
      <div className="p-3 border-t border-zinc-100 bg-white relative z-40" ref={userMenuRef}>
        {userMenuOpen && (
          <div
            className={`absolute bottom-full mb-2 bg-white border border-zinc-200 rounded-xl shadow-xl py-1.5 z-50 transition-all ${
              isCollapsed ? "left-12 w-48" : "left-3 right-3"
            }`}
          >
            <div className="px-3 py-1.5 border-b border-zinc-100">
              <p className="text-[11px] font-bold text-zinc-900 truncate">
                {user?.last_name}, {user?.first_name}
              </p>
              <p className="text-[10px] text-zinc-400 truncate">{user?.email}</p>
            </div>

            <div className="p-1 space-y-0.5">
              <Link
                to="/my/profile"
                onClick={() => setUserMenuOpen(false)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg transition-colors text-left"
              >
                <FiUser className="w-3.5 h-3.5 text-zinc-400" />
                <span>My Profile</span>
              </Link>
              <div className="border-t border-zinc-100 my-1" />
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
              >
                <FiLogOut className="w-3.5 h-3.5" />
                <span>Log Out</span>
              </button>
            </div>
          </div>
        )}

        <button
          onMouseEnter={(e) => handleMouseEnter(e, `${user?.first_name || "User"} (${user?.email || "Account"})`)}
          onMouseLeave={handleMouseLeave}
          onClick={() => {
            setUserMenuOpen(!userMenuOpen);
            setActiveTooltip(null);
          }}
          className="w-full flex items-center justify-between p-1 rounded-lg hover:bg-zinc-50 transition-colors"
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md bg-zinc-200 text-zinc-700 flex items-center justify-center font-bold text-[11px] shrink-0">
              {user?.first_name ? user.first_name.slice(0, 2).toUpperCase() : "AH"}
            </div>
            {!isCollapsed && (
              <div className="min-w-0 text-left">
                <p className="text-xs font-semibold text-zinc-900 truncate leading-tight">
                  {user?.last_name ? `${user.last_name}, ${user.first_name}` : "User Profile"}
                </p>
                <p className="text-[10px] text-zinc-400 truncate leading-tight">
                  {user?.email || ""}
                </p>
              </div>
            )}
          </div>
          {!isCollapsed && (
            <FiChevronUp
              className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${
                userMenuOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
      </div>

      {/* Floating Tooltip */}
      {isCollapsed && activeTooltip && (
        <div
          style={{
            top: `${activeTooltip.top}px`,
            left: `${activeTooltip.left}px`,
          }}
          className="fixed -translate-y-1/2 z-[9999] pointer-events-none flex items-center gap-1.5 transition-all duration-150 ease-out animate-in fade-in zoom-in-95"
        >
          <div className="w-2 h-2 bg-zinc-900 border-l border-b border-zinc-700/60 rotate-45 -mr-1 shrink-0" />
          <div className="bg-zinc-900/90 backdrop-blur-md text-zinc-100 border border-zinc-700/60 shadow-xl shadow-black/20 rounded-lg px-3 py-1.5 text-[11px] font-semibold tracking-wide whitespace-nowrap flex items-center gap-2">
            <span>{activeTooltip.title}</span>
          </div>
        </div>
      )}

    </aside>
  );
}